import { apiCall } from 'src/utils/ApiCall';
import { RecordAttributes } from '@amzn/controllershiphub-typescript-client';
import { filterAndCleanRecords } from 'src/components/pages/SummaryPage';
import { CONTROL_APIS, ApiError } from 'src/common/config/ApiConstants';

interface GLDataResponse {
  records: RecordAttributes[][];
  totalRecords: number;
  searchAfter?: string[];
  distinctValues?: Record<string, string[]>;
}

export const getGLControlData = async (
  controlId: string,
  filters: Record<string, string[]> = {},
  searchAfter?: string[]
): Promise<GLDataResponse> => {
  try {
    const body: Record<string, string> = {};

    if (Object.keys(filters).length > 0) {
      body.filters = JSON.stringify(filters);
    }

    if (searchAfter && searchAfter.length > 0) {
      body.searchAfter = searchAfter.join(',');
    }

    const response = await apiCall<{
      records: RecordAttributes[][];
      searchAfter?: string[];
      totalRecords: number;
    }>({
      endpoint: `/GetGLControlData/${controlId}`,
      method: 'POST',
      body,
    });

    const cleanedRecords = filterAndCleanRecords(response.records || []);

    return {
      records: cleanedRecords,
      totalRecords: response.totalRecords || 0,
      searchAfter: response.searchAfter,
    };
  } catch (err: unknown) {
    const error = err as Error;
    if (error instanceof ApiError && error.status === 401) {
      throw new Error(CONTROL_APIS.ERROR_MESSAGES.UNAUTHORIZED);
    }
    throw new Error('Failed to fetch GL control data');
  }
};

export const getDistinctValuesForFilter = async (
  controlId: string,
  filter: string,
  selectedFilters?: Record<string, string[]>,
  country?: string,
  selectedRegions?: string[]
): Promise<string[]> => {
  try {
    const queryParams: Record<string, string> = { filter };

    // Add country parameter for region filtering
    if (country) {
      queryParams.country = country;
    }

    // Add selectedRegions parameter for country_name filtering
    if (selectedRegions && selectedRegions.length > 0) {
      queryParams.selectedRegions = selectedRegions.join(',');
    }

    // Add selected filter values for cascading filters
    if (selectedFilters) {
      if (selectedFilters['Period Name']?.length > 0) {
        queryParams.periodName = selectedFilters['Period Name'][0];
      }
      if (selectedFilters['Country Name']?.length > 0) {
        queryParams.countryName = selectedFilters['Country Name'][0];
      }
    }

    const response = await apiCall<{
      distinctValues: string[];
    }>({
      endpoint: `/GetDistinctValuesForFilter/${controlId}`,
      queryParams,
    });

    let distinctValues = response.distinctValues || [];

    // Client-side filtering as a fallback for account filter
    if (filter.toLowerCase() === 'account_number') {
      distinctValues = distinctValues.filter(
        (account) => account && account.toString().startsWith('6')
      );
    }

    return distinctValues;
  } catch (err: unknown) {
    const error = err as Error;
    if (error instanceof ApiError && error.status === 401) {
      throw new Error(CONTROL_APIS.ERROR_MESSAGES.UNAUTHORIZED);
    }
    throw new Error(`Failed to fetch distinct values for filter: ${filter}`);
  }
};

export const updateGLExport = async (
  controlId: string,
  filters: Record<string, string[]>,
  exportName: string
): Promise<{ status: string; message: string }> => {
  try {
    const response = await apiCall<{
      status: string;
      message: string;
    }>({
      endpoint: `/UpdateGLExport/${controlId}`,
      method: 'POST',
      body: {
        filters: JSON.stringify(filters),
        exportName,
      },
    });

    return response;
  } catch (err: unknown) {
    const error = err as Error;
    if (error instanceof ApiError && error.status === 401) {
      throw new Error(CONTROL_APIS.ERROR_MESSAGES.UNAUTHORIZED);
    }
    throw new Error('Failed to initiate GL export');
  }
};

export const getGLExports = async (
  controlId: string
): Promise<{
  records: Array<{
    exportId?: string;
    exportName?: string;
    status?: string;
    createdAt?: string;
    downloadUrl?: string;
    expiresAt?: string;
  }>;
  message: string;
}> => {
  try {
    const response = await apiCall<{
      records: Array<{
        exportId?: string;
        exportName?: string;
        status?: string;
        createdAt?: string;
        downloadUrl?: string;
        expiresAt?: string;
      }>;
      message: string;
    }>({
      endpoint: `/GetGLExports/${controlId}`,
    });

    return response;
  } catch (err: unknown) {
    const error = err as Error;
    if (error instanceof ApiError && error.status === 401) {
      throw new Error(CONTROL_APIS.ERROR_MESSAGES.UNAUTHORIZED);
    }
    throw new Error('Failed to fetch GL exports');
  }
};
